import { createContext, useContext } from 'react';

/**
 * Creates a context with a default value of null.
 * Returns a provider and a hook to use the context.
 * The hook throws an error if the context is null, which is useful for ensuring
 * that the context is used within a provider.
 */
export function createSafeContext<ContextValue>(
  displayName: string,
  errorMessage: string,
) {
  const Context = createContext<ContextValue | null>(null);
  Context.displayName = displayName;

  const useSafeContext = () => {
    const ctx = useContext(Context);

    if (ctx === null) {
      throw new Error(errorMessage);
    }

    return ctx;
  };

  const Provider = ({
    children,
    value,
  }: {
    value: ContextValue;
    children: React.ReactNode;
  }) => <Context.Provider value={value}>{children}</Context.Provider>;

  return [Provider, useSafeContext] as const;
}
